/**
 * Módulo Trámites Fiscales — registro independiente (paridad con Visor / Laboral / Diseñador).
 */
import { Navigate } from "react-router-dom";
import PanelGeneral from "@/pages/PanelGeneral";
import Clientes from "@/pages/Clientes";
import Ayuda from "@/pages/Ayuda";
import TramitesFiscalesPage from "@/pages/tramites/TramitesFiscalesPage";
import TramitesHubPage from "@/pages/tramites/TramitesHubPage";
import TramitesValidadorNss from "@/pages/tramites/TramitesValidadorNss";
import TramitesInscripcionTrabajadoresSat from "@/pages/tramites/TramitesInscripcionTrabajadoresSat";
import TramitesInscripcionTrabajadoresImss from "@/pages/tramites/TramitesInscripcionTrabajadoresImss";
import DatosMaestrosHub from "@/pages/DatosMaestrosHub";
import DatosMaestrosValidadorCheckIdPage from "@/pages/datosMaestros/DatosMaestrosValidadorCheckIdPage";
import DatosMaestrosValidacionRfcSatPage from "@/pages/datosMaestros/DatosMaestrosValidacionRfcSatPage";
import { VALIDADOR_DATOS_CHECK_ID_LABEL } from "@/lib/checkIdLabels";
import {
  DATOS_MAESTROS_CHECK_ID_PATH,
  DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
} from "@/modules/datosMaestros/datosMaestrosHub";
import { buildModuloFromTabs, createModuloTab } from "@/modules/moduloTabRegistry";
import {
  MODULO_TRAMITES_CARD,
  MODULO_TRAMITES_ID,
  TRAMITES_CONTEXT_LABEL,
  getTramitesSidebarNavItems,
} from "@/modules/tramites/manifest";
import { MATERIALIDAD_EXPEDIENTE_PATH } from "@/modules/materialidad/manifest";
import {
  TRAMITES_LEGACY_REDIRECT_TARGETS,
  TRAMITES_LEGACY_PATHS,
  TRAMITES_INSCRIPCION_IMSS_PATH,
  TRAMITES_INSCRIPCION_SAT_PATH,
  TRAMITES_MATERIALIDAD_PATH,
  TRAMITES_MODULE_PATHS,
  TRAMITES_VALIDACION_RFC_SAT_PATH,
  buildTramitesUrl,
} from "@/modules/tramites/tramitesService";

const TRAMITES_TABS = [
  createModuloTab({
    index: true,
    path: "/",
    label: "Panel General",
    iconKey: "panel",
    end: true,
    showInNav: false,
    testid: "nav-panel-general",
    component: <PanelGeneral />,
  }),
  createModuloTab({
    path: "/tramites/inicio",
    label: TRAMITES_CONTEXT_LABEL,
    iconKey: "tramites",
    testid: "nav-tramites-inicio",
    component: <TramitesHubPage />,
  }),
  createModuloTab({
    path: "/datos-maestros",
    label: "Datos Maestros",
    description: "Catálogo fiscal compartido: empresas, FIEL y certificados por contribuyente.",
    iconKey: "database",
    testid: "nav-tramites-datos-maestros",
    component: <DatosMaestrosHub />,
  }),
  createModuloTab({
    path: DATOS_MAESTROS_CHECK_ID_PATH,
    label: VALIDADOR_DATOS_CHECK_ID_LABEL,
    iconKey: "validate",
    showInNav: false,
    hubOnlyNav: true,
    testid: "nav-datos-maestros-check-id",
    component: <DatosMaestrosValidadorCheckIdPage />,
  }),
  createModuloTab({
    path: DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
    label: "Validación de RFC SAT",
    iconKey: "validate",
    showInNav: false,
    hubOnlyNav: true,
    testid: "nav-datos-maestros-validacion-rfc-sat",
    component: <DatosMaestrosValidacionRfcSatPage />,
  }),
  createModuloTab({
    path: "/tramites/validacion-datos",
    label: VALIDADOR_DATOS_CHECK_ID_LABEL,
    iconKey: "validate",
    showInNav: false,
    testid: "nav-tramites-validacion-datos",
    component: <Navigate to={DATOS_MAESTROS_CHECK_ID_PATH} replace />,
  }),
  createModuloTab({
    path: "/clientes",
    label: "Clientes",
    description: "Contribuyentes fiscales compartidos (RFC, contacto, certificados). Mismo catálogo que Visor y Laboral.",
    iconKey: "clients",
    hubOnlyNav: true,
    testid: "nav-tramites-clientes",
    component: <Clientes />,
  }),
  createModuloTab({
    path: "/tramites/validador-nss",
    label: "Validador NSS (IMSS / IDSE)",
    description: "Validación local de NSS, RFC, CURP y consistencia antes de trámites IMSS/IDSE.",
    iconKey: "validate",
    testid: "nav-tramites-validador-nss",
    component: <TramitesValidadorNss />,
  }),
  createModuloTab({
    path: TRAMITES_VALIDACION_RFC_SAT_PATH,
    label: "Validación de RFC SAT",
    iconKey: "validate",
    showInNav: false,
    testid: "nav-tramites-validacion-rfc-sat",
    component: <Navigate to={DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH} replace />,
  }),
  createModuloTab({
    path: TRAMITES_INSCRIPCION_SAT_PATH,
    label: "Inscripción de trabajadores SAT",
    description: "Generación de archivos y cartas para alta en el portal del SAT.",
    iconKey: "userplus",
    showInNav: false,
    testid: "nav-tramites-inscripcion-sat",
    component: <TramitesInscripcionTrabajadoresSat />,
  }),
  createModuloTab({
    path: TRAMITES_INSCRIPCION_IMSS_PATH,
    label: "Inscripción de trabajadores IMSS",
    description: "Alta patronal, NSS, SDI y movimientos afiliatorios.",
    iconKey: "users",
    showInNav: false,
    testid: "nav-tramites-inscripcion-imss",
    component: <TramitesInscripcionTrabajadoresImss />,
  }),
  createModuloTab({
    path: TRAMITES_MATERIALIDAD_PATH,
    label: "Materialidad",
    description: "Redirige al módulo Materialidad.",
    iconKey: "folder",
    showInNav: false,
    hubCard: false,
    testid: "nav-tramites-materialidad",
    component: <Navigate to={MATERIALIDAD_EXPEDIENTE_PATH} replace />,
  }),
  createModuloTab({
    path: "/tramites-fiscales",
    label: TRAMITES_CONTEXT_LABEL,
    iconKey: "tramites",
    showInNav: false,
    testid: "nav-tramites-fiscales",
    component: <TramitesFiscalesPage />,
  }),
  createModuloTab({
    path: "/ayuda",
    label: "Ayuda y Manuales",
    iconKey: "help",
    hubOnlyNav: true,
    showInNav: false,
    component: <Ayuda />,
  }),
];

function legacyRedirectElement(path) {
  const target = TRAMITES_LEGACY_REDIRECT_TARGETS[path];
  if (!target) return <Navigate to="/tramites/inicio" replace />;
  return <Navigate to={target} replace />;
}

const TRAMITES_EXTRA_ROUTES = [
  ...Object.keys(TRAMITES_LEGACY_REDIRECT_TARGETS).map((path) => ({
    path: path.replace(/^\//, ""),
    element: legacyRedirectElement(path),
  })),
];

export const MODULO_TRAMITES = (() => {
  const mod = buildModuloFromTabs({
    id: MODULO_TRAMITES_ID,
    card: MODULO_TRAMITES_CARD,
    contextLabel: TRAMITES_CONTEXT_LABEL,
    tabs: TRAMITES_TABS,
    extraRoutes: TRAMITES_EXTRA_ROUTES,
  });
  const baseGetNavItems = mod.getNavItems.bind(mod);
  mod.getNavItems = () => [
    ...baseGetNavItems(),
    ...getTramitesSidebarNavItems(buildTramitesUrl),
  ];
  return mod;
})();

/** Paths permitidos cross-módulo (redirects legacy + rutas propias). */
export function isTramitesCrossModulePath(pathname) {
  const path = String(pathname || "").split("?")[0];
  return TRAMITES_LEGACY_PATHS.has(path) || TRAMITES_MODULE_PATHS.has(path);
}

export const getModuloTramitesNavItems = () => MODULO_TRAMITES.getNavItems();
export const getModuloTramitesPaths = () => MODULO_TRAMITES.getPaths();
export const getModuloTramitesDefaultRoute = () => MODULO_TRAMITES.getDefaultRoute();
export const isModuloTramitesPath = (pathname) => MODULO_TRAMITES.isPathAllowed(pathname);
export const getModuloTramitesContextLabel = () => MODULO_TRAMITES.getContextLabel();
export const getModuloTramitesRoutes = () => MODULO_TRAMITES.getRoutes();
